import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Button, SelectField, StandardDialog, Tabs } from '@roc/ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useDebtTrackingStore } from '../../hooks/useDebtTrackingStore';
import { FinancingAmountChange, LoanDebtDetail } from '../../utils/debtTrackingConstants';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  formatMoney,
  formatWarehouseLabel,
  isValidAmount,
  todayApiDate,
} from '../../utils/debtTrackingUtils';
import { DebtEntryFields, DebtEntryFormValues } from './debtEntryFields';

const useStyles = makeStyles((theme: Theme) => ({
  headerCell: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  amount: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  increase: {
    color: theme.palette.success.main,
    whiteSpace: 'nowrap',
  },
  decrease: {
    color: theme.palette.error.main,
    whiteSpace: 'nowrap',
  },
  footer: {
    color: theme.palette.text.secondary,
    flex: 1,
  },
}));

enum DetailTab {
  ENTRY = 0,
  HISTORY = 1,
}

const SOURCE_LABELS: Record<string, string> = { manual: 'Manual', bulk_import: 'Bulk import' };

// The Entry tab edits the loan's current financing amount: it starts with the current value, and saving REPLACES
// it (a new effective date defaults to today).
const formFromLoan = (loan: LoanDebtDetail): DebtEntryFormValues => ({
  amount: loan?.financingAmount ?? '',
  effectiveDate: todayApiDate(),
  comment: '',
});

export const LoanDebtDetailDialog = observer(({ recordedByName }: { recordedByName: string }) => {
  const classes = useStyles();
  const { debtTrackingStore } = useDebtTrackingStore();
  const { selectedLoanId, loanDetail, loanHistory, selectedWarehouse, warehouses } = debtTrackingStore;
  const [tab, setTab] = useState(DetailTab.ENTRY);
  const [values, setValues] = useState<DebtEntryFormValues>(formFromLoan(null));
  const [warehouseId, setWarehouseId] = useState<number>(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTab(DetailTab.ENTRY);
    setError('');
  }, [selectedLoanId]);

  // Pre-fill (and re-fill after each save) with the loan's current financing amount.
  useEffect(() => {
    setValues(formFromLoan(loanDetail));
    setWarehouseId(loanDetail?.warehouse?.id ?? null);
  }, [loanDetail]);

  // A loan can move to another warehouse when its saved financing amount is 0 (it moves, then gets the typed amount
  // there) or when the typed amount is 0 (it is set to 0, then moves). The API records the move and the amount change
  // as two history entries.
  const warehouseChanged = !!loanDetail && warehouseId !== loanDetail.warehouse?.id;
  const savedAmountIsZero = !!loanDetail && Number(loanDetail.financingAmount) === 0;
  const typedAmountIsZero = isValidAmount(values.amount) && Number(values.amount) === 0;
  const canChangeWarehouse = !!loanDetail && (savedAmountIsZero || typedAmountIsZero);
  const moveBlocked = warehouseChanged && !canChangeWarehouse;
  const canSubmit = isValidAmount(values.amount) && !!values.effectiveDate && !!warehouseId && !moveBlocked && !isSaving;

  const onFieldChange = (field: keyof DebtEntryFormValues, value: DebtEntryFormValues[keyof DebtEntryFormValues]) => {
    setValues(current => ({ ...current, [field]: value }));
    setError('');
  };

  const submit = async () => {
    setIsSaving(true);
    const errorMessage = await flowResult(debtTrackingStore.updateLoanFinancingAmount({
      financingAmount: Number(values.amount),
      effectiveDate: values.effectiveDate,
      comment: values.comment.trim(),
    }, warehouseId));
    setIsSaving(false);
    if (errorMessage) {
      setError(errorMessage);
    } else {
      setTab(DetailTab.HISTORY);
    }
  };

  const renderChangeCell = (change: FinancingAmountChange) => {
    if (change.change === null || change.change === undefined) {
      return <em>Initial amount</em>;
    }
    const className = change.change > 0 ? classes.increase : change.change < 0 ? classes.decrease : '';
    return <span className={className}>{formatMoney(change.change, true)}</span>;
  };

  const renderChangeRow = (change: FinancingAmountChange) => (
    <TableRow key={change.historyId}>
      <TableCell>{formatDisplayDate(change.effectiveDate)}</TableCell>
      <TableCell>{change.warehouseName ?? '-'}</TableCell>
      <TableCell className={classes.amount}>{formatMoney(change.financingAmount)}</TableCell>
      <TableCell>{renderChangeCell(change)}</TableCell>
      <TableCell>{change.comment || <em>-</em>}</TableCell>
      <TableCell>{SOURCE_LABELS[change.source] ?? change.source ?? '-'}</TableCell>
      <TableCell>{formatDisplayDateTime(change.recordedAt)}</TableCell>
      <TableCell>{change.recordedBy}</TableCell>
    </TableRow>
  );

  const historyTable = loanHistory.length === 0 ? (
    <Typography color="textSecondary">No changes yet.</Typography>
  ) : (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {['Effective Date', 'Warehouse', 'Financing Amount', 'Change', 'Comment', 'Source', 'Recorded', 'By'].map(header => (
            <TableCell key={header} className={classes.headerCell}>{header}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{loanHistory.map(renderChangeRow)}</TableBody>
    </Table>
  );

  return (
    <StandardDialog
      open={!!selectedLoanId}
      title={`Loan ${selectedLoanId ?? ''}`}
      subTitle={formatWarehouseLabel(loanDetail?.warehouse ?? selectedWarehouse)}
      handleClose={() => debtTrackingStore.closeLoanDetail()}
      maxWidth="md"
      removeContentBox
      dialogContent={
        <Box m={2} flexGrow={1} minWidth={0}>
          <Box pb={2}>
            <Tabs
              selectedTab={tab}
              tabs={[{ label: 'Entry' }, { label: `History (${loanHistory.length})` }]}
              onTabChange={(e, index) => setTab(index)}
            />
          </Box>
          {tab === DetailTab.ENTRY && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SelectField
                  testId="debtEntryWarehouse"
                  label="Warehouse"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={!canChangeWarehouse}
                  helperText={savedAmountIsZero
                    ? 'The financing amount is 0, so the loan can move to another warehouse with a new amount.'
                    : canChangeWarehouse
                      ? 'The new financing amount is 0, so the loan can move to another warehouse.'
                      : 'Set the financing amount to 0 to move the loan to another warehouse.'}
                  value={warehouseId ?? ''}
                  options={warehouses.map(warehouse => ({ label: warehouse.name, value: warehouse.id }))}
                  onChange={value => {
                    setWarehouseId(value);
                    setError('');
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <DebtEntryFields values={values} onChange={onFieldChange} recordedByName={recordedByName} />
              </Grid>
              {moveBlocked && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      The loan has a financing amount greater than 0 in {loanDetail.warehouse?.name}, so it can't move
                      with a new amount. Set the financing amount to 0 to move it (then give it its new amount), or
                      switch back to {loanDetail.warehouse?.name}.
                    </Typography>
                  </Alert>
                </Grid>
              )}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error"><Typography variant="body2">{error}</Typography></Alert>
                </Grid>
              )}
            </Grid>
          )}
          {tab === DetailTab.HISTORY && <Box maxHeight={400} overflow="auto">{historyTable}</Box>}
        </Box>
      }
      dialogActions={
        <>
          <Typography variant="body2" className={classes.footer}>
            {loanDetail && <>
              Current <strong>{formatMoney(loanDetail.financingAmount)}</strong>
              {' · effective '}{formatDisplayDate(loanDetail.effectiveDate)}
            </>}
          </Typography>
          <Button testId="closeLoanDebtDetail" variant="outlined" color="primary" onClick={() => debtTrackingStore.closeLoanDetail()}>
            Close
          </Button>
          {tab === DetailTab.ENTRY && (
            <Button testId="submitDebtEntry" variant="contained" color="primary" disabled={!canSubmit} onClick={submit}>
              Update
            </Button>
          )}
        </>
      }
    />
  );
});
